import { X, AlertCircle } from 'lucide-react';

interface ValidationError {
    sheet: string;
    row: number;
    message: string;
}

interface ValidationErrorsDialogProps {
    isOpen: boolean;
    onClose: () => void;
    errors: ValidationError[];
}

export function ValidationErrorsDialog({ isOpen, onClose, errors }: ValidationErrorsDialogProps) {
    if (!isOpen || errors.length === 0) return null;

    // Group errors by sheet
    const errorsBySheet = errors.reduce((acc, error) => {
        if (!acc[error.sheet]) {
            acc[error.sheet] = [];
        }
        acc[error.sheet].push(error);
        return acc;
    }, {} as Record<string, ValidationError[]>);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                            <AlertCircle className="w-5 h-5 text-red-600" />
                        </div>
                        <div>
                            <h2 className="text-xl font-semibold text-slate-900">Validation Failed</h2>
                            <p className="text-sm text-slate-600 mt-0.5">
                                {errors.length} error{errors.length !== 1 ? 's' : ''} found in the uploaded file
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-slate-400 hover:text-slate-600 p-2 rounded hover:bg-slate-100 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="px-6 py-4 flex-1 overflow-y-auto">
                    <div className="space-y-6">
                        {Object.entries(errorsBySheet).map(([sheetName, sheetErrors]) => (
                            <div key={sheetName}>
                                <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                                    <span className="px-2 py-1 bg-slate-100 rounded text-sm">
                                        {sheetName}
                                    </span>
                                    <span className="text-sm text-slate-500 font-normal">
                                        ({sheetErrors.length} error{sheetErrors.length !== 1 ? 's' : ''})
                                    </span>
                                </h3>
                                <div className="space-y-2">
                                    {sheetErrors.map((error, index) => (
                                        <div
                                            key={index}
                                            className="flex gap-3 p-3 bg-red-50 border border-red-200 rounded-lg"
                                        >
                                            <div className="shrink-0">
                                                <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-red-100 text-red-700 text-xs font-semibold">
                                                    {error.row}
                                                </span>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm text-red-900">{error.message}</p>
                                                <p className="text-xs text-red-600 mt-0.5">Row {error.row}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-slate-200 bg-slate-50">
                    <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                        <div className="flex-1 text-sm text-slate-600">
                            <p className="font-medium text-slate-900 mb-1">How to fix:</p>
                            <ul className="list-disc list-inside space-y-1">
                                <li>Open the Excel file and check the highlighted rows</li>
                                <li>Fix the errors according to the messages above</li>
                                <li>Save the file and try importing again</li>
                            </ul>
                        </div>
                    </div>
                    <div className="flex justify-end mt-4">
                        <button onClick={onClose} className="btn btn-primary px-6 py-2">
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
